// ═══════════════════════════════════════════════════════════════════════════════
// MXDR365 — Theme Constants (Dark + Light mode)
// ═══════════════════════════════════════════════════════════════════════════════

export type ThemeMode = 'dark' | 'light';

export const darkTheme = {
  mode: 'dark' as ThemeMode,

  // Backgrounds
  bg: '#080c14',
  bg2: '#0c1220',
  card: '#111827',
  cardHover: '#162033',
  sidebar: '#0a0f1a',
  input: '#141c2e',
  border: '#1c2740',

  // Text
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textTertiary: '#556478',

  // Accent — derived from brand navy #1a3a5c
  accent: '#4a8fe2',
  accentDark: '#3a7bc8',
  accentGlow: 'rgba(74,143,226,0.12)',
  // Text placed ON an accent-filled surface (primary buttons, filled chips)
  accentText: '#0a1120',

  // Sidebar-scoped tokens. The sidebar surface is dark in BOTH themes (it
  // carries the reversed white logo), so it needs its own text/border/accent
  // colors that stay readable when the rest of the app flips to light mode.
  sidebarText: '#e2e8f0',
  sidebarTextSecondary: '#94a3b8',
  sidebarTextTertiary: '#556478',
  sidebarBorder: '#1c2740',
  sidebarAccent: '#4a8fe2',
  sidebarAccentGlow: 'rgba(74,143,226,0.12)',

  // Chrome (scrollbars, text selection, keyboard focus ring)
  scrollbar: '#1e2d48',
  scrollbarHover: '#2d4068',
  selection: 'rgba(74,143,226,0.25)',
  focusRing: 'rgba(74,143,226,0.6)',

  // Status colors
  red: '#ef4444',
  redGlow: 'rgba(239,68,68,0.12)',
  green: '#22c55e',
  greenGlow: 'rgba(34,197,94,0.12)',
  yellow: '#f59e0b',
  yellowGlow: 'rgba(245,158,11,0.12)',
  blue: '#3b82f6',
  blueGlow: 'rgba(59,130,246,0.1)',
  purple: '#a855f7',
  purpleGlow: 'rgba(168,85,247,0.1)',
  orange: '#f97316',
  pink: '#ec4899',
} as const;

export const lightTheme = {
  mode: 'light' as ThemeMode,

  // Backgrounds
  bg: '#f0f4f8',
  bg2: '#e8eef5',
  card: '#ffffff',
  cardHover: '#f5f8fc',
  sidebar: '#1e293b',
  input: '#f1f5f9',
  border: '#cbd5e1',

  // Text
  text: '#0f172a',
  textSecondary: '#475569',
  textTertiary: '#64748b', // slate-500 — #94a3b8 fell below 3:1 on white

  // Accent — derived from brand navy #1a3a5c
  accent: '#2d6ab5',
  accentDark: '#1f5494',
  accentGlow: 'rgba(45,106,181,0.1)',
  // Light-mode accent is a mid navy — needs white text, not the dark navy
  // used on the brighter dark-mode accent
  accentText: '#ffffff',

  // Sidebar stays dark in light mode (reversed white logo lives there), so
  // these mirror dark-surface values rather than the light text palette.
  sidebarText: '#e2e8f0',
  sidebarTextSecondary: '#94a3b8',
  sidebarTextTertiary: '#7b8ba1',
  sidebarBorder: '#334155',
  sidebarAccent: '#6ba3e8',
  sidebarAccentGlow: 'rgba(107,163,232,0.15)',

  // Chrome (scrollbars, text selection, keyboard focus ring)
  scrollbar: '#c3cede',
  scrollbarHover: '#a8b8cc',
  selection: 'rgba(45,106,181,0.2)',
  focusRing: 'rgba(45,106,181,0.55)',

  // Status colors — one shade darker than the dark-mode set so small badge
  // text keeps ~4.5:1 contrast on white cards
  red: '#dc2626',
  redGlow: 'rgba(220,38,38,0.1)',
  green: '#15803d',
  greenGlow: 'rgba(21,128,61,0.1)',
  yellow: '#b45309',
  yellowGlow: 'rgba(180,83,9,0.1)',
  blue: '#2563eb',
  blueGlow: 'rgba(37,99,235,0.08)',
  purple: '#9333ea',
  purpleGlow: 'rgba(147,51,234,0.08)',
  orange: '#c2410c',
  pink: '#db2777',
} as const;

// Resolve the persisted theme mode at MODULE EVALUATION time. theme.ts is a
// dependency of every page/component module, so this runs before any of them
// evaluate — module-scope style constants (`const inputStyle = { background:
// theme.input, ... }`) capture the correct palette from the first paint.
// Must stay in sync with getPersistedThemeSchedule/resolveActiveTheme in
// useAppState.tsx (same 'mxdr365_theme' localStorage key).
function getInitialMode(): ThemeMode {
  try {
    const saved = localStorage.getItem('mxdr365_theme');
    if (saved) {
      const s = JSON.parse(saved) as { mode?: string; lightStartTime?: string; lightEndTime?: string };
      if (s.mode === 'light') return 'light';
      if (s.mode === 'auto') return getAutoTheme(s.lightStartTime, s.lightEndTime);
    }
  } catch { /* no storage access — fall through to dark */ }
  return 'dark';
}

// The live theme object. Every component imports this (or aliases it as
// `const t = theme`), so applyThemeMode() swaps its values IN PLACE — all
// existing references pick up the new palette on the next render.
export const theme: typeof darkTheme =
  getInitialMode() === 'light' ? { ...(lightTheme as unknown as typeof darkTheme) } : { ...darkTheme };
export type ThemeColor = keyof typeof darkTheme;

// ─── Swap the live theme (and derived color maps) to the given mode ───
// Called by AppProvider during render whenever state.activeThemeMode changes.
export function applyThemeMode(mode: ThemeMode): void {
  const palette = mode === 'light' ? (lightTheme as unknown as typeof darkTheme) : darkTheme;
  if (theme.mode === palette.mode) return;
  Object.assign(theme, palette);
  Object.assign(statusColors, buildStatusColors(palette));
  Object.assign(priorityColors, buildPriorityColors(palette));
  Object.assign(shiftColors, buildShiftColors(palette));
  Object.assign(ticketStatusColors, buildTicketStatusColors(palette));
  Object.assign(changeRiskColors, buildChangeRiskColors(palette));
}

// ─── Elevation / shadows ───
export const shadows = {
  sm:  '0 1px 4px rgba(0,0,0,0.2)',
  md:  '0 2px 10px rgba(0,0,0,0.35)',
  lg:  '0 8px 32px rgba(0,0,0,0.55)',
  xl:  '0 16px 48px rgba(0,0,0,0.65)',
} as const;

// ─── Font imports (load in index.html or via @import) ───
export const fonts = {
  primary: "'Inter', system-ui, -apple-system, sans-serif",
  mono: "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
  weights: { light: 300, regular: 400, medium: 500, semibold: 600, bold: 700, extrabold: 800 },
} as const;

// ─── Spacing scale ───
export const spacing = {
  xs: 4, sm: 8, md: 12, lg: 16, xl: 20, xxl: 24, xxxl: 28,
} as const;

// ─── Border radius ───
export const radii = {
  sm: 4, md: 8, lg: 10, xl: 12, pill: 99,
} as const;

// ─── Task / PM color maps ───
// Built via functions so applyThemeMode() can rebuild them per palette.
const buildStatusColors = (t: typeof darkTheme): Record<string, string> => ({
  todo: t.textTertiary,
  progress: t.blue,
  review: t.purple,
  done: t.green,
  blocked: t.red,
  'off-track': t.orange,
});
export const statusColors: Record<string, string> = buildStatusColors(theme);

export const statusLabels: Record<string, string> = {
  todo: 'To Do',
  progress: 'In Progress',
  review: 'In Review',
  done: 'Done',
  blocked: 'Blocked',
  'off-track': 'Off Track',
};

const buildPriorityColors = (t: typeof darkTheme): Record<string, string> => ({
  critical: t.red,
  high: t.orange,
  medium: t.yellow,
  low: t.green,
});
export const priorityColors: Record<string, string> = buildPriorityColors(theme);

export const priorityIcons: Record<string, string> = {
  critical: '🔴',
  high: '🟠',
  medium: '🟡',
  low: '🟢',
};

const buildShiftColors = (t: typeof darkTheme): Record<string, string> => ({
  'Shift 1': t.blue,
  'Shift 2': t.yellow,
  'Shift 3': t.purple,
});
export const shiftColors: Record<string, string> = buildShiftColors(theme);

export const shiftTimeLabels: Record<string, string> = {
  'Shift 1': '2 AM – 10 AM CST',
  'Shift 2': '10 AM – 6 PM CST',
  'Shift 3': '6 PM – 2 AM CST',
};

// ─── Ticket color maps ───
const buildTicketStatusColors = (t: typeof darkTheme): Record<string, string> => ({
  new: t.blue,
  assigned: t.purple,
  in_progress: t.yellow,
  pending: t.orange,
  on_hold: '#6b7280',
  escalated: t.red,
  resolved: t.green,
  closed: t.textTertiary,
  cancelled: t.textTertiary,
});
export const ticketStatusColors: Record<string, string> = buildTicketStatusColors(theme);
export const ticketStatusLabels: Record<string, string> = {
  new: 'New', assigned: 'Assigned', in_progress: 'In Progress', pending: 'Pending',
  on_hold: 'On Hold', escalated: 'Escalated', resolved: 'Resolved', closed: 'Closed', cancelled: 'Cancelled',
};
export const ticketStatusIcons: Record<string, string> = {
  new: '🔵', assigned: '🟣', in_progress: '🟡', pending: '🟠',
  on_hold: '⏸️', escalated: '🔴', resolved: '✅', closed: '⬛', cancelled: '❌',
};
export const ticketTypeIcons: Record<string, string> = {
  incident: '🚨', service_request: '📋', change_request: '🔄', problem: '🔧',
};
export const ticketTypeLabels: Record<string, string> = {
  incident: 'Incident', service_request: 'Service Request', change_request: 'Change Request', problem: 'Problem',
};
export const ticketTypePrefixes: Record<string, string> = {
  incident: 'INC', service_request: 'SRQ', change_request: 'CHG', problem: 'PRB',
};
export const ticketSourceLabels: Record<string, string> = {
  portal: 'Portal', email: 'Email', teams: 'Teams', phone: 'Phone', auto: 'Auto-detected', api: 'API',
};
export const ticketSourceIcons: Record<string, string> = {
  portal: '🌐', email: '📧', teams: '💬', phone: '📞', auto: '🤖', api: '⚡',
};
export const ticketCategoryLabels: Record<string, string> = {
  security_incident: 'Security Incident', threat_hunting: 'Threat Hunting', access_management: 'Access Management',
  endpoint: 'Endpoint', email_security: 'Email Security', identity: 'Identity', cloud_security: 'Cloud Security',
  configuration: 'Configuration', monitoring: 'Monitoring', compliance: 'Compliance',
  onboarding: 'Onboarding', license: 'Licensing', other: 'Other',
};
export const ticketCategoryIcons: Record<string, string> = {
  security_incident: '🛡️', threat_hunting: '🔍', access_management: '🔑', endpoint: '💻',
  email_security: '📧', identity: '👤', cloud_security: '☁️', configuration: '⚙️',
  monitoring: '📊', compliance: '📋', onboarding: '🎓', license: '🔐', other: '📦',
};
export const impactLabels: Record<string, string> = { critical: 'Org-Wide', high: 'Department', medium: 'Group', low: 'Individual' };
export const urgencyLabels: Record<string, string> = { critical: 'Immediate', high: 'Urgent', medium: 'Normal', low: 'Planned' };
const buildChangeRiskColors = (t: typeof darkTheme): Record<string, string> => ({ high: t.red, medium: t.yellow, low: t.green, none: t.textTertiary });
export const changeRiskColors: Record<string, string> = buildChangeRiskColors(theme);
export const timelineEventIcons: Record<string, string> = {
  comment: '💬', status_change: '🔄', assignment: '👤', priority_change: '⚡', escalation: '🔺',
  sla_breach: '⏰', merge: '🔗', link: '🔗', approval: '✅', field_change: '📝', time_logged: '⏱️',
  watcher_added: '👁️', reopen: '🔓',
};
export const SLA_TARGETS: Record<string, { response: number; resolution: number; policy: string }> = {
  critical: { response: 15, resolution: 240, policy: 'Critical Incident' },
  high: { response: 30, resolution: 480, policy: 'High Priority' },
  medium: { response: 120, resolution: 1440, policy: 'Standard' },
  low: { response: 480, resolution: 4320, policy: 'Low Priority' },
};

// ─── Get active theme based on mode ───
export function getTheme(mode: 'dark' | 'light'): typeof darkTheme {
  return mode === 'light' ? (lightTheme as unknown as typeof darkTheme) : darkTheme;
}

// ─── Auto-schedule helper: returns 'light' or 'dark' based on time ───
export function getAutoTheme(lightStart = '08:00', lightEnd = '20:00'): 'dark' | 'light' {
  const now = new Date();
  const [sh, sm] = lightStart.split(':').map(Number);
  const [eh, em] = lightEnd.split(':').map(Number);
  const startMins = sh * 60 + sm;
  const endMins = eh * 60 + em;
  const nowMins = now.getHours() * 60 + now.getMinutes();
  return nowMins >= startMins && nowMins < endMins ? 'light' : 'dark';
}
